import Loader from 'monday-ui-react-core/dist/Loader';
import React, { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import SignDateIcon from '../../assets/icons/calendar.svg';
import CheckboxIcon from '../../assets/icons/checkbox.svg';
import DateIcon from '../../assets/icons/date.svg';
import DropdownBoxIcon from '../../assets/icons/dropdown-box.svg';
import DropdownColumn from '../../assets/icons/dropdown.svg';
import FormulaIcon from '../../assets/icons/formula.svg';
import HashIcon from '../../assets/icons/hash.svg';
import InitialsIcon from '../../assets/icons/initials.svg';
import LineItemIcon from '../../assets/icons/listItem.svg';
import LocationIcon from '../../assets/icons/location.svg';
import MirrorIcon from '../../assets/icons/mirror.svg';
import NumberIcon from '../../assets/icons/number.svg';
import PhoneIcon from '../../assets/icons/phone.svg';
import SignatureIcon from '../../assets/icons/signature.svg';
import StatusColumn from '../../assets/icons/status.svg';
import TextIcon from '../../assets/icons/text.svg';
import PeopleIcon from '../../assets/icons/people.svg';
import TextBoxIcon from '../../assets/icons/textbox.svg';
import ImageIcon from '../../assets/icons/prefilled-image.svg';
import LinkIcon from '../../assets/icons/link.svg';
import ImageAddIcon from '../../assets/icons/image-add.svg';
import WalletIcon from '../../assets/icons/wallet.svg';
import Calendar from '../../assets/icons/datepicker.svg';

import { useTranslation } from 'react-i18next';
import APP_CONFIG from '../../config';
import { FileContext } from '../../context/FileContext';
import {
  getBatchedSubitemFormula,
  getItemDetails,
  getSubItems,
} from '../../services/boards';
import DragableButton from '../DraggableButton';
import { supportedColumnsFromMonday } from '../../config/common';
import CustomTipseen from '../CustomTipseen/CustomTipseen';
import Tipseen from 'monday-ui-react-core/dist/Tipseen';
import TipseenContent from 'monday-ui-react-core/dist/TipseenContent';
import { useMondayStore } from '../../store/monday.store';
import {
  isFeatureAvailable,
  FEATURES,
} from '../../constants/featureBasedPricing';
import { Skeleton } from '@vibe/core';
import { FORM_FIELD } from '@/constants/constants';

const iconMapper = {
  text: TextIcon,
  people: PeopleIcon,
  numbers: NumberIcon,
  date: DateIcon,
  phone: PhoneIcon,
  item_id: HashIcon,
  location: LocationIcon,
  long_text: TextIcon,
  email: TextIcon,
  mirror: MirrorIcon,
  board_relation: MirrorIcon,
  formula: FormulaIcon,
  status: StatusColumn,
  dropdown: DropdownColumn,
  file: ImageIcon,
  link: LinkIcon,
};

const SideBarContainer = styled.section`
  /* position: absolute; */
  /* right: 0; */
  background-color: #f5f6f8;
  padding: 23px 0;
  width: 20vw;
  height: calc(100vh - 66px);
  overflow: auto;
  filter: drop-shadow(-3px 4px 8px rgba(197, 199, 208, 0.5));
`;

const Title = styled.div`
  padding: 16px 16px 0 0;
  width: 80%;
  margin: 0 auto;
  margin-bottom: 10px;
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.5px;
  color: #323338;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 80%;
  margin: 0 auto;

  .buttons {
    justify-content: flex-start;
    min-width: 225px !important;
    background-color: #e6e9ef;
    color: black;
    font-size: 14px;
    border: 1px solid transparent;
    height: 38px;

    &:hover {
      background-color: #cce5ff;
      border: 1px solid #0073ea;
    }
  }
`;

// Comment

const fetchBatchedHydratedFormula = async (
  itemId,
  defaultState,
  setter,
  batch = 1,
  batchSize = 50,
) => {
  const latestState = defaultState();
  if (latestState.length === 0) {
    setter('stop');
    return false;
  }
  const start = (batch - 1) * batchSize;
  const end = start + batchSize;
  const currentBatch = latestState.slice(start, end);

  if (currentBatch.length === 0) {
    setter('stop');
    return;
  } // Base case: no more batches

  getBatchedSubitemFormula(itemId, batch, batchSize).then((res) => {
    if (res?.length > 0) {
      const updatedList = defaultState().map((item) => {
        const foundItem = res.find((i) => i.id === item.id);
        return foundItem
          ? { ...item, column_values: foundItem.column_values }
          : item;
      });

      setter(updatedList);
    } else {
      setter('stop');
      return;
    }
  });

  // Recurse for the next batch
  fetchBatchedHydratedFormula(
    itemId,
    defaultState,
    setter,
    batch + 1,
    batchSize,
  );
};

const Sidebar = ({ isGeneratePDFPage, isV3 = false }) => {
  const { t } = useTranslation();
  const {
    fileId,
    boardId,
    itemId,
    isInitialLoad,
    columnValuesMap,
    setColumnValuesMap,
    setBoardName,
    updateSubItems,
    setBoardData,
    pricingPlan,
    paymentFieldAdded,
    slug,
  } = useContext(FileContext);

  const queryClient = useQueryClient();

  const [showTipseen, setShowTipseen] = useState(true);

  // const includesPaymentItem = addedFormFields.some(
  //   (i) => i.itemId === 'sd-payment',
  // );

  const { data: res, isLoading } = useQuery(
    ['get-columns', itemId, fileId],
    () => getItemDetails(fileId, itemId),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchIntervalInBackground: true,
      refetchInterval: 10000,
      enabled: !!itemId && !!boardId,
      onSuccess: (res) => {
        console.log(res,'res')
        if (res?.status === 401) {
          localStorage.setItem('fileId', fileId);
        }
        const item = res?.data?.items?.[0] ?? res;
        setBoardName(item?.board?.name || '');
        item?.column_values?.forEach((columnValue) => {
          if (!columnValuesMap.has(columnValue.id)) {
            setColumnValuesMap(
              new Map(columnValuesMap.set(columnValue.id, columnValue)),
            );
          }
        });

        setBoardData(item?.board || null);

        setColumnValuesMap(
          new Map(
            columnValuesMap.set('item-name', {
              id: 'item-name',
              text: item?.name || '',
              title: 'Item Name',
              type: 'text',
            }),
          ),
        );
      },
    },
  );

  const isLineItemFeatureAvailable = isFeatureAvailable(
    pricingPlan,
    FEATURES.LINE_ITEMS,
  );

  const { isLoading: fetchingSubItems } = useQuery({
    queryKey: ['sub-items', itemId],
    queryFn: () => getSubItems(itemId),
    onSuccess: (res) => {
      const initialSubitems = res?.data?.items?.[0]?.subitems || [];
      updateSubItems(initialSubitems);
      useMondayStore.getState().setSubItems(initialSubitems);
      fetchBatchedHydratedFormula(
        itemId,
        () => useMondayStore.getState().subitems,
        (newData) => {
          if (newData === 'stop') {
            return;
          }
          useMondayStore.getState().setSubItems(newData);
        },
      );
    },
    enabled: !!itemId && isLineItemFeatureAvailable,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const fileDetails = queryClient.getQueryData(['get-file', fileId]);
  const item = res?.data?.items?.[0] ?? res;
  return (
    <SideBarContainer>
      {!isGeneratePDFPage ? (
        <>
          <Title>{t('Signature Fields')}</Title>
          {!isLoading ? (
            <ButtonGroup>
              <DragableButton
                className="buttons"
                key="signature"
                icon={SignatureIcon}
                type="Column"
                column={{
                  ...APP_CONFIG.form_fields.signatureField,
                }}
              >
                {t('Signature')}
              </DragableButton>
              <DragableButton
                className="buttons"
                icon={InitialsIcon}
                key="initials"
                type="Column"
                column={{
                  ...APP_CONFIG.form_fields.initialsField,
                }}
              >
                {t('Initials')}
              </DragableButton>
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              <Loader size={Loader.sizes.SMALL} />
            </ButtonGroup>
          )}
        </>
      ) : null}

      <Title>{t('Board fields')}</Title>
      <ButtonGroup>
        {!isLineItemFeatureAvailable ? (
          <CustomTipseen
            content="This feature isn't available with your current plan. Please upgrade to
          use this and other exciting features."
            submitButtonText="Upgrade"
            theme="primary"
            submitButtonColor="#FFD383"
            submitButtonTextColor="#000"
            handleSubmit={() =>
              window.open(
                `https://${slug}.monday.com/apps/installed_apps/10050849`,
              )
            }
          >
            <DragableButton
              className="buttons"
              key="lineitems"
              icon={LineItemIcon}
              type="Column"
              column={{
                ...APP_CONFIG.form_fields.lineItem,
              }}
              disabled
              isPaidFeature
            >
              {t('Sub Items')}
            </DragableButton>
          </CustomTipseen>
        ) : !fetchingSubItems ? (
          <DragableButton
            className="buttons"
            key="lineitems"
            icon={LineItemIcon}
            type="Column"
            column={{
              ...APP_CONFIG.form_fields.lineItem,
            }}
            disabled={!isLineItemFeatureAvailable}
          >
            {t('Sub Items')}
          </DragableButton>
        ) : (
          <Skeleton width={225} height={38} className="tw-block" />
        )}
        {item?.name && (
          <>
            {showTipseen && isInitialLoad ? (
              <Tipseen
                className="custom__tipseen"
                hideCloseButton
                content={
                  <span>
                    <TipseenContent
                      submitButtonText={'Dismiss'}
                      onSubmit={() => setShowTipseen(false)}
                    >
                      Drag and drop item name and other board fields to populate
                      it in your document.
                    </TipseenContent>
                  </span>
                }
                theme="primary"
              >
                <DragableButton
                  key="item"
                  type="Column"
                  icon={TextIcon}
                  column={{
                    ...APP_CONFIG.form_fields.itemName,
                    value: item?.name,
                  }}
                >
                  {t('Item Name')}
                </DragableButton>
              </Tipseen>
            ) : (
              <DragableButton
                key="item"
                type="Column"
                icon={TextIcon}
                column={{
                  ...APP_CONFIG.form_fields.itemName,
                  value: item?.name,
                }}
              >
                {t('Item Name')}
              </DragableButton>
            )}
          </>
        )}
        {item?.column_values
          ?.filter((column) => supportedColumnsFromMonday.includes(column?.type))
          .map((column) => (
            <DragableButton
              key={column.id}
              icon={iconMapper[column.type]}
              column={column}
              type="Column"
            >
              {column.title || column.column?.title}
            </DragableButton>
          ))}
      </ButtonGroup>

      {!isGeneratePDFPage ? (
        <>
          <Title>{t('Standard Fields')}</Title>

          <ButtonGroup>
            {!isFeatureAvailable(pricingPlan, FEATURES.CHECKBOX) ? (
              <CustomTipseen
                content="This feature isn't available with your current plan. Please upgrade to
  use this and other exciting features."
                submitButtonText="Upgrade"
                theme="primary"
                submitButtonColor="#FFD383"
                submitButtonTextColor="#000"
                handleSubmit={() =>
                  window.open(
                    `https://${slug}.monday.com/apps/installed_apps/10050849`,
                  )
                }
              >
                <DragableButton
                  className="buttons"
                  key="checkbox"
                  icon={CheckboxIcon}
                  type="Column"
                  column={{
                    ...APP_CONFIG.form_fields.checkbox,
                  }}
                  disabled
                  isPaidFeature
                >
                  {t('Checkbox')}
                </DragableButton>
              </CustomTipseen>
            ) : (
              <DragableButton
                className="buttons"
                key="checkbox"
                icon={CheckboxIcon}
                type="Column"
                column={{
                  ...APP_CONFIG.form_fields.checkbox,
                }}
              >
                {t('Checkbox')}
              </DragableButton>
            )}

            {!isFeatureAvailable(pricingPlan, FEATURES.TEXT_BOX) ? (
              <CustomTipseen
                content="This feature isn't available with your current plan. Please upgrade to
    use this and other exciting features."
                submitButtonText="Upgrade"
                theme="primary"
                submitButtonColor="#FFD383"
                submitButtonTextColor="#000"
                handleSubmit={() =>
                  window.open(
                    `https://${slug}.monday.com/apps/installed_apps/10050849`,
                  )
                }
              >
                <DragableButton
                  className="buttons"
                  key="text-box"
                  icon={TextBoxIcon}
                  type="Column"
                  column={{
                    ...APP_CONFIG.form_fields.textBox,
                  }}
                  disabled
                  isPaidFeature
                >
                  {t('Text Box')}
                </DragableButton>
              </CustomTipseen>
            ) : (
              <DragableButton
                className="buttons"
                key="text-box"
                icon={TextBoxIcon}
                type="Column"
                column={{
                  ...APP_CONFIG.form_fields.textBox,
                }}
              >
                {t('Text Box')}
              </DragableButton>
            )}

            {!isFeatureAvailable(pricingPlan, FEATURES.TEXT_AREA) ? (
              <CustomTipseen
                content="This feature isn't available with your current plan. Please upgrade to
    use this and other exciting features."
                submitButtonText="Upgrade"
                theme="primary"
                submitButtonColor="#FFD383"
                submitButtonTextColor="#000"
                handleSubmit={() =>
                  window.open(
                    `https://${slug}.monday.com/apps/installed_apps/10050849`,
                  )
                }
              >
                <DragableButton
                  className="buttons"
                  key="sd-textarea"
                  icon={TextIcon}
                  type="Column"
                  column={{
                    ...APP_CONFIG.form_fields.textArea,
                  }}
                  disabled
                  isPaidFeature
                >
                  {t('Text Area')}
                </DragableButton>
              </CustomTipseen>
            ) : (
              <DragableButton
                className="buttons"
                key="sd-textarea"
                icon={TextIcon}
                type="Column"
                column={{
                  ...APP_CONFIG.form_fields.textArea,
                }}
              >
                {t('Text Area')}
              </DragableButton>
            )}

            <DragableButton
              className="buttons"
              key="sign-date"
              icon={SignDateIcon}
              type="Column"
              column={{
                ...APP_CONFIG.form_fields.signDate,
              }}
            >
              {t('Sign Date')}
            </DragableButton>

            <DragableButton
              className="buttons"
              key="sd-datePicker"
              icon={Calendar}
              type="Column"
              column={{
                ...APP_CONFIG.form_fields.standard_fields.datePicker,
              }}
              height="20px"
            >
              {t('Date Picker')}
            </DragableButton>

            {!isFeatureAvailable(pricingPlan, FEATURES.DROPDOWN) ? (
              <CustomTipseen
                content="This feature isn't available with your current plan. Please upgrade to
    use this and other exciting features."
                submitButtonText="Upgrade"
                theme="primary"
                submitButtonColor="#FFD383"
                submitButtonTextColor="#000"
                handleSubmit={() =>
                  window.open(
                    `https://${slug}.monday.com/apps/installed_apps/10050849`,
                  )
                }
              >
                <DragableButton
                  className="buttons"
                  key="sd-dropdown"
                  icon={DropdownBoxIcon}
                  type="Column"
                  column={{
                    ...APP_CONFIG.form_fields.dropdown,
                  }}
                  disabled
                  isPaidFeature
                >
                  {t('Dropdown')}
                </DragableButton>
              </CustomTipseen>
            ) : (
              <DragableButton
                className="buttons"
                key="sd-dropdown"
                icon={DropdownBoxIcon}
                type="Column"
                column={{
                  ...APP_CONFIG.form_fields.dropdown,
                }}
              >
                {t('Dropdown')}
              </DragableButton>
            )}

            <DragableButton
              className="buttons"
              key="sd-link"
              icon={LinkIcon}
              type="Column"
              column={{
                ...APP_CONFIG.form_fields.link,
              }}
            >
              {t('Link')}
            </DragableButton>

            <DragableButton
              className="buttons"
              key="sd-image"
              icon={ImageAddIcon}
              type="Column"
              column={{
                ...APP_CONFIG.form_fields.image,
              }}
            >
              {t('Image')}
            </DragableButton>
            {fileDetails?.data?.isStripeConnected || isV3 ? (
              !isFeatureAvailable(pricingPlan, FEATURES.PAYMENT) ? (
                <CustomTipseen
                  content="This feature isn't available with your current plan. Please upgrade to
    use this and other exciting features."
                  submitButtonText="Upgrade"
                  theme="primary"
                  submitButtonColor="#FFD383"
                  submitButtonTextColor="#000"
                  handleSubmit={() =>
                    window.open(
                      `https://${slug}.monday.com/apps/installed_apps/10050849`,
                    )
                  }
                >
                  <DragableButton
                    className="buttons"
                    key="sd-payment"
                    icon={WalletIcon}
                    type="Column"
                    column={{
                      ...APP_CONFIG.form_fields.payment,
                    }}
                    disabled
                    isPaidFeature
                  >
                    {t('Payment Item')}
                  </DragableButton>
                </CustomTipseen>
              ) : (
                <DragableButton
                  className="buttons"
                  key="sd-payment"
                  disabled={!!paymentFieldAdded}
                  icon={WalletIcon}
                  type="Column"
                  column={{
                    ...APP_CONFIG.form_fields.payment,
                  }}
                >
                  {t('Payment Item')}
                </DragableButton>
              )
            ) : null}
            <DragableButton
              className="buttons"
              key={FORM_FIELD.FILE}
              icon={TextIcon}
              type="Column"
              column={{
                title: 'File Upload',
                type: FORM_FIELD.FILE,
                id: FORM_FIELD.FILE,
              }}
            >
              File Upload
            </DragableButton>
          </ButtonGroup>
        </>
      ) : null}
    </SideBarContainer>
  );
};

export default React.memo(Sidebar);
